import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { VenueData, TicketGroupData } from '../types';

// Validation schema for the ticket form
// Create validation schema function to handle conditional price validation
const createValidationSchema = (
  isTicketFree: boolean,
  isGroupTicket: boolean,
  hasMultipleVenues: boolean,
  hasMultipleTicketGroups: boolean,
  isOfferActive: boolean,
  ticketOrder: boolean
) =>
  Yup.object({
    ticketType: Yup.string().required('Ticket name is required'),

    price: isTicketFree
      ? Yup.number().optional()
      : Yup.number().required('Ticket amount is required').min(0),

    groupTicketCount: isGroupTicket
      ? Yup.number()
          .required('Number of people is required')
          .min(2, 'Group capacity must be at least 2 attendees')
      : Yup.number().optional(),

    venueId: Yup.string()
      .test(
        'venue-required',
        'Venue selection is required',
        value => !hasMultipleVenues || !!value
      ),

    groupingId: Yup.string()
      .test(
        'group-required',
        'Ticket group selection is required',
        value => !hasMultipleTicketGroups || !!value
      ),

    maxCount: isGroupTicket
      ? Yup.number()
          .required('Number of group tickets available is required')
          .typeError('Available tickets must be a number')
          .min(0, 'Quantity must be 0 or greater')
      : Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            String(originalValue).trim() === '' ? null : value
          )
          .typeError('Available tickets must be a number')
          .min(0, 'Quantity must be 0 or greater'),

    previousAmount: isOfferActive
      ? Yup.number()
          .required('Previous amount is required when offer is active')
          .min(0, 'Previous amount must be 0 or greater')
      : Yup.number().optional(),
    ticketOrder :Yup.number().min(1,'Must be 1 or greater')
  });




type NewTicketModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: any) => void;
  isLoading?: boolean;
  ticketKind: 'single' | 'group';
  hasMultipleVenues?: boolean;
  venues?: VenueData[];
  hasMultipleTicketGroups?: boolean;
  ticketGroups?: TicketGroupData[];
  isTicketFree?: boolean;
  editingTicket?: any;
  type ?: boolean;
  ticketOrder?: any;
};

const NewTicketModal: React.FC<NewTicketModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  ticketKind,
  hasMultipleVenues = false,
  venues = [],
  hasMultipleTicketGroups = false,
  ticketGroups = [],
  isTicketFree = false,
  editingTicket,
  type,
  ticketOrder,
}) => {

  // State for offer checkbox
  const [isOfferActive, setIsOfferActive] = useState(editingTicket?.isOfferActive || false);

  // Initialize formik with validation
 const validation = useFormik({
  enableReinitialize: true,
  initialValues: {
    ticketType: editingTicket?.name || '',
    description: editingTicket?.description || '',
    price: editingTicket?.price?.toString() || '',
    maxCount: editingTicket?.maxCount?.toString() || '',
    isGroupTicket: editingTicket?.isGroupTicket,
    groupTicketCount: editingTicket?.groupTicketCount?.toString() || '',
    venueId: editingTicket?.venueId || '',
    groupingId: editingTicket?.groupingId || '',
    isOfferActive: editingTicket?.isOfferActive || false,
    previousAmount: editingTicket?.previousAmount?.toString() || '',
    ticketOrder: editingTicket?.ticketOrder?.toString() || '1',
  },
  validationSchema: createValidationSchema(
    isTicketFree,
    ticketKind === 'group',
    hasMultipleVenues,
    hasMultipleTicketGroups,
    isOfferActive,
    ticketOrder
  ),
  validateOnBlur: true,
  validateOnChange: true,
  onSubmit: (values) => {
    const formData = {
      ...values,
      ticketType: values.ticketType.trim(),
      maxCount: parseInt(values.maxCount) || null,
      price: isTicketFree ? 0 : parseFloat(values.price),
      groupTicketCount:
        ticketKind === 'group' ? parseInt(values.groupTicketCount) : undefined,
      venueId: hasMultipleVenues ? values.venueId : undefined,
      groupingId: hasMultipleTicketGroups ? values.groupingId : undefined,
      id: editingTicket?.id,
      isOfferActive: isOfferActive,
      previousAmount: isOfferActive ? parseFloat(values.previousAmount) : undefined
    };
    onSubmit(formData);
  }
});


  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
      setIsOfferActive(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update isOfferActive when editingTicket changes
  React.useEffect(() => {
    if (editingTicket) {
      setIsOfferActive(editingTicket.isOfferActive || false);
    }
  }, [editingTicket]);

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <h4 className="mb-0">
          {(type === true || ticketKind === 'group') ? 'Group Ticket' : 'Single Ticket'}
        </h4>
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody className="px-4">
          {/* Ticket Name Field */}
          <FormGroup>
            <Label className="form-label">Ticket Name <span className="text-danger">*</span></Label>
            <Input
              name="ticketType"
              type="text"
              placeholder="Enter Ticket Name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.ticketType}
              invalid={
                validation.touched.ticketType &&
                !!validation.errors.ticketType
              }
            />
            {validation.touched.ticketType &&
              validation.errors.ticketType ? (
              <FormFeedback type="invalid">
                { validation.errors.ticketType as string}
              </FormFeedback>
            ) : null}
          </FormGroup>

          {/* Venue Selection Field - Only show if hasMultipleVenues is true */}
          {hasMultipleVenues && venues.length > 0 && (
            <FormGroup>
              <Label className="form-label">Select Venue<span className="text-danger"> *</span></Label>
              <Input
                name="venueId"
                type="select"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.venueId}
                invalid={
                  validation.touched.venueId &&
                  !!validation.errors.venueId
                }
              >
                <option value="">Select a venue</option>
                {venues.map((venue) => (
                  <option key={venue.venueId} value={venue.venueId}>
                    {venue.venueName}
                  </option>
                ))}
              </Input>
              {validation.touched.venueId &&
                validation.errors.venueId ? (
                <FormFeedback type="invalid">
                  { validation.errors.venueId as string}
                </FormFeedback>
              ) : null}
            </FormGroup>
          )}

          {/* Ticket Group Selection Field - Only show if hasMultipleTicketGroups is true */}
          {hasMultipleTicketGroups && ticketGroups.length > 0 && (
            <FormGroup>
              <Label className="form-label">Select Ticket Group<span className="text-danger"> *</span></Label>
              <Input
                name="groupingId"
                type="select"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.groupingId}
                invalid={
                  validation.touched.groupingId &&
                  !!validation.errors.groupingId
                }
              >
                <option value="">Select a ticket group</option>
                {ticketGroups.map((ticketGroup) => (
                  <option key={ticketGroup.id} value={ticketGroup.id}>
                    {ticketGroup.groupName}
                  </option>
                ))}
              </Input>
              {validation.touched.groupingId &&
                validation.errors.groupingId ? (
                <FormFeedback type="invalid">
                  {validation.errors.groupingId as string}
                </FormFeedback>
              ) : null}
            </FormGroup>
          )}

          

          {/* Ticket Amount Field - Only for Paid Tickets */}
          {!isTicketFree && (
            <FormGroup>
              <Label className="form-label">
                {isOfferActive ? 'Offer Amount' : 'Ticket Amount'} <span className="text-danger">*</span>
              </Label>
              <Input
                name="price"
                type="number"
                placeholder="Enter Ticket Amount"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.price}
                invalid={
                  validation.touched.price &&
                  !!validation.errors.price
                }
              />
              {validation.touched.price &&
                validation.errors.price ? (
                <FormFeedback type="invalid">
                  { validation.errors.price as string}
                </FormFeedback>
              ) : null}
            </FormGroup>
          )}

          {/* Free Ticket Indicator */}
          {isTicketFree && (
            <FormGroup>
              <div className="alert alert-success" role="alert">
                <i className="fa-solid fa-gift me-2"></i>
                <strong>Free Ticket:</strong> This ticket will be free for attendees.
              </div>
            </FormGroup>
          )}

          {/* Is Offer Checkbox - Only for Paid Tickets */}
          {/* {!isTicketFree && (
            <FormGroup>
              <div className="d-flex align-items-center">
                <label className="btn-switch m-0 me-3">
                  <input
                    type="checkbox"
                    checked={isOfferActive}
                    onChange={(e) => {
                      setIsOfferActive(e.target.checked);
                      validation.setFieldValue('isOfferActive', e.target.checked);
                      if (!e.target.checked) {
                        validation.setFieldValue('previousAmount', '');
                      }
                    }}
                  />
                  <span className="checkbox-slider"></span>
                </label>
                <Label className="mb-0">
                  <strong>Is Offer</strong>
                </Label>
              </div>
              <small className="text-muted mt-1 d-block">
                Enable this to show a discounted price offer for this ticket
              </small>
            </FormGroup>
          )} */}

          {/* Previous Amount Field - Only show when Is Offer is active */}
          {!isTicketFree && isOfferActive && (
            <FormGroup>
              <Label className="form-label">
                Previous Amount <span className="text-danger">*</span>
              </Label>
              <Input
                name="previousAmount"
                type="number"
                placeholder="Enter Previous Amount"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.previousAmount}
                invalid={
                  validation.touched.previousAmount &&
                  !!validation.errors.previousAmount
                }
              />
              {validation.touched.previousAmount &&
                validation.errors.previousAmount ? (
                <FormFeedback type="invalid">
                  { validation.errors.previousAmount as string}
                </FormFeedback>
              ) : null}
              <small className="text-muted mt-1">
                This will be shown as the original price before the discount
              </small>
            </FormGroup>
          )}

          {/* Max Count Field */}
          <FormGroup>
            <Label className="form-label">
              {ticketKind === 'group' ? (
                <>
                  Number of Group Tickets Available <span className="text-danger">*</span>
                </>
              ) : (
                'Ticket Quantity'
              )}
            </Label>
            <Input
              name="maxCount"
              type="number"
              placeholder="Enter 0 for unlimited availability."
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.maxCount}
              invalid={
                validation.touched.maxCount &&
                !!validation.errors.maxCount
              }
            />
            {validation.touched.maxCount &&
              validation.errors.maxCount ? (
              <FormFeedback type="invalid">
                { validation.errors.maxCount as string}
              </FormFeedback>
            ) : null}
            <small className="text-muted mt-1 d-block">
              Enter the total number of tickets available for sale. Enter 0 for unlimited availability.
            </small>
          </FormGroup>

          {/* Group Ticket Count Field - Only show if group ticket is selected */}
          {ticketKind === 'group' && (
            <FormGroup>
              <Label>Attendee per Group <span className="text-danger">*</span>
              </Label>
              <Input
                name="groupTicketCount"
                type="number"
                placeholder="Enter the number of attendees per group"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.groupTicketCount}
                invalid={
                  validation.touched.groupTicketCount &&
                  !!validation.errors.groupTicketCount
                }
              />
              {validation.touched.groupTicketCount &&
                validation.errors.groupTicketCount ? (
                <FormFeedback type="invalid">
                  { validation.errors.groupTicketCount as string}
                </FormFeedback>
              ) : null}
            </FormGroup>
          )}
          {/* Ticket Order Field */}
          <FormGroup>
            <Label className="form-label">Ticket Order</Label>
            <Input
              name="ticketOrder"
              type="text"
              placeholder="Enter Ticket Order"
              value={validation.values.ticketOrder}
              rows={4}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.ticketOrder && !!validation.errors.ticketOrder}
            />
            {validation.touched.ticketOrder && validation.errors.ticketOrder && (
              <FormFeedback type="invalid">
                  { validation.errors.ticketOrder as string}
              </FormFeedback>
            )}
          </FormGroup>
          {/* Ticket Description Field */}
          <FormGroup>
            <Label className="form-label">Ticket Description</Label>
            <Input
              name="description"
              type="textarea"
              placeholder="Enter Ticket Description"
              value={validation.values.description}
              rows={4}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.description && !!validation.errors.description}
            />
            {validation.touched.description && validation.errors.description && (
              <FormFeedback type="invalid">
                  { validation.errors.description as string}
              </FormFeedback>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter className="new-ticket-modal-footer d-flex gap-2">
          <Button
            className="btn mt-3 flex-fill h_50"
            color="secondary"
            type="button"
            outline
            onClick={toggle}
            disabled={isLoading}
          >
            <h5 className="mb-0">Cancel</h5>
          </Button>
          <Button
            className="btn btn-primary mt-3 flex-fill h_50"
            color="primary"
            type="submit"
            disabled={isLoading}
          >
            <h5 className="mb-0">
              {isLoading
                ? editingTicket ? "Saving..." : "Creating..."
                : editingTicket ? "Save Changes" : "Create Ticket"}
            </h5>
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default NewTicketModal;
